import type { ReactNode } from 'react';
import type { AxonEvent } from '@reflex/shared';
import { cn } from '@reflex/ui/lib/utils';
import { Timestamp } from '@reflex/ui/components/shared/Timestamp';
import { StreamBubbleContainer } from './StreamBubbleContainer';
import { EventDetailTooltip } from './EventMeta';

type BubbleColor = 'amber' | 'red' | 'blue' | 'indigo' | 'teal' | 'purple' | 'violet';

const COLOR_STYLES: Record<
  BubbleColor,
  { border: string; bg: string; iconBg: string; iconText: string; titleText: string }
> = {
  amber: {
    border: 'border-status-warning/40',
    bg: 'bg-status-warning/5',
    iconBg: 'bg-status-warning/20',
    iconText: 'text-status-warning',
    titleText: 'text-status-warning',
  },
  red: {
    border: 'border-status-error/40',
    bg: 'bg-status-error/5',
    iconBg: 'bg-status-error/20',
    iconText: 'text-status-error',
    titleText: 'text-status-error',
  },
  blue: {
    border: 'border-status-info/40',
    bg: 'bg-status-info/5',
    iconBg: 'bg-status-info/20',
    iconText: 'text-status-info',
    titleText: 'text-status-info',
  },
  indigo: {
    border: 'border-status-accent-indigo/40',
    bg: 'bg-status-accent-indigo/5',
    iconBg: 'bg-status-accent-indigo/20',
    iconText: 'text-status-accent-indigo',
    titleText: 'text-status-accent-indigo',
  },
  teal: {
    border: 'border-status-accent-teal/40',
    bg: 'bg-status-accent-teal/5',
    iconBg: 'bg-status-accent-teal/20',
    iconText: 'text-status-accent-teal',
    titleText: 'text-status-accent-teal',
  },
  purple: {
    border: 'border-status-accent-purple/40',
    bg: 'bg-status-accent-purple/5',
    iconBg: 'bg-status-accent-purple/20',
    iconText: 'text-status-accent-purple',
    titleText: 'text-status-accent-purple',
  },
  violet: {
    border: 'border-status-accent-violet/30',
    bg: 'bg-status-accent-violet/5',
    iconBg: 'bg-status-accent-violet/20',
    iconText: 'text-status-accent-violet',
    titleText: 'text-status-accent-violet',
  },
};

export function StreamCardBubble({
  icon,
  rawIcon,
  color,
  title,
  timestamp,
  event,
  children,
  className,
  'data-testid': testId,
}: {
  icon?: ReactNode;
  /** When true, render the icon directly without the colored background wrapper. */
  rawIcon?: boolean;
  /** When set, apply accent border/bg/title styles. When omitted, use the plain card look. */
  color?: BubbleColor;
  title?: string;
  timestamp?: number;
  event?: AxonEvent;
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  const styles = color ? COLOR_STYLES[color] : null;
  const showIcon = Boolean(icon);

  return (
    <StreamBubbleContainer
      className={cn(
        'rounded-xl border p-4',
        styles ? [styles.border, styles.bg] : 'bg-card',
        className,
      )}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        {icon ? (
          styles && !rawIcon ? (
            <div
              className={cn('mt-0.5 shrink-0 rounded-full p-1.5', styles.iconBg, styles.iconText)}
            >
              {icon}
            </div>
          ) : (
            <div className="mt-0.5 shrink-0">{icon}</div>
          )
        ) : null}
        <div className="min-w-0 flex-1">
          {title ? <p className={cn('text-xs font-medium', styles?.titleText)}>{title}</p> : null}
          {children}
        </div>
      </div>
      {timestamp != null || event ? (
        <div className={cn('mt-2 flex items-center gap-1.5', showIcon && 'pl-10')}>
          {timestamp != null ? <Timestamp timestamp={timestamp} /> : null}
          {event ? <EventDetailTooltip event={event} /> : null}
        </div>
      ) : null}
    </StreamBubbleContainer>
  );
}
